import { Property, RoomDimensionUnit } from '@propertypal/shared/src/types/property';
import { getGalleryImages } from '@propertypal/shared/src/utils/property';
import { useRouter } from 'next/router';
import React, { FunctionComponent } from 'react';
import { Heading } from '../typography';
import {
  RoomCard,
  RoomContent,
  RoomDetails,
  RoomDescription,
  RoomImage,
  RoomImageWrapper,
  RoomMeta,
  RoomName,
  RoomsList,
} from './PropertyRooms.style';

type PropertyImages = Property['images'];
type PropertyImage = Exclude<PropertyImages, undefined>[number];

interface Props {
  property: Property;
}

const getImageById = (images: PropertyImages, imageId: number | null | undefined): PropertyImage | undefined => {
  if (imageId == null || !images) return undefined;
  return images?.find((img) => img.id === imageId);
};

const formatDimensions = (
  width: number | null | undefined,
  length: number | null | undefined,
  unit: RoomDimensionUnit | null | undefined,
): string | null => {
  if (width == null || length == null) return null;

  if (unit === 'FEET') {
    const toFeetAndInches = (value: number): string => {
      const [feet, inches] = `${value}`.split('.');
      if (!inches) return `${feet}'`;

      const normalizedInches = inches.replace(/0+$/, '');
      return normalizedInches ? `${feet}'${normalizedInches}"` : `${feet}'`;
    };

    return `${toFeetAndInches(width)} x ${toFeetAndInches(length)}`;
  }

  return `${width}m x ${length}m`;
};

const PropertyRooms: FunctionComponent<Props> = ({ property }) => {
  const router = useRouter();
  const images = property.images || [];
  const rooms = property.rooms || [];
  const sortedRooms = [...rooms].sort((a, b) => a.displayOrder - b.displayOrder);
  const galleryImages = getGalleryImages(property, 'hd', true, true, false, true);

  return (
    <>
      <Heading as="h3" fontSize={20} mt={20}>
        Rooms
      </Heading>
      <RoomsList role="list" data-testid="property-rooms">
        {sortedRooms.map((room) => {
          const image = getImageById(images, room.imageId);
          const dimensions = formatDimensions(room.dimensionWidth, room.dimensionLength, room.dimensionUnit);
          const hasImage = !!image;
          const galleryImage = galleryImages.find((img) => img.imageId === room.imageId);

          return (
            <RoomCard key={`${room.name}-${room.displayOrder}`} $hasImage={hasImage} role="listitem">
              {image && (
                <RoomImageWrapper
                  type="button"
                  onClick={() => {
                    router.push(
                      `${property.path}/slideshow${!galleryImage?.index ? '' : `/photo-${galleryImage.index + 1}`}`,
                      undefined,
                      {
                        shallow: true,
                      },
                    );
                  }}
                >
                  <RoomImage
                    src={image.urls['580x425:FILL_CROP'] || image.url}
                    alt={`${room.name} photo`}
                    loading="lazy"
                  />
                </RoomImageWrapper>
              )}
              <RoomContent>
                <RoomName $mb={room.description && !(room.level || dimensions) ? 20 : undefined}>{room.name}</RoomName>
                {(!!room.level || !!dimensions || !!room.description) && (
                  <RoomDetails>
                    {(dimensions || room.level) && (
                      <RoomMeta>
                        {dimensions && <>{dimensions}</>}
                        {room.level && (
                          <>
                            {!!dimensions && ' '}({room.level})
                          </>
                        )}
                      </RoomMeta>
                    )}
                    {room.description && <RoomDescription html={room.description} />}
                  </RoomDetails>
                )}
              </RoomContent>
            </RoomCard>
          );
        })}
      </RoomsList>
    </>
  );
};

export default PropertyRooms;
